import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Button, Form, Message } from 'semantic-ui-react';
import { request } from './helpers';
import { dateRange, financeQuery, financeYuan } from './finance';

export default function FinancialOverview({ refresh }) {
  const [draft, setDraft] = useState(() => ({
    ...dateRange(),
    user_id: '',
    model: '',
    channel_id: '',
  }));
  const [filters, setFilters] = useState(draft),
    [data, setData] = useState(null),
    [error, setError] = useState(''),
    [loading, setLoading] = useState(false);
  const latest = useRef(0);
  useEffect(() => {
    const version = ++latest.current;
    setLoading(true);
    setError('');
    setData(null);
    let query;
    try {
      query = financeQuery(filters);
    } catch (e) {
      setError(e.message);
      setLoading(false);
      return;
    }
    request('get', `/api/platform/admin/finance?${query}`)
      .then((value) => {
        if (version === latest.current) setData(value);
      })
      .catch((e) => {
        if (version === latest.current) setError(e.message);
      })
      .finally(() => {
        if (version === latest.current) setLoading(false);
      });
    return () => {
      latest.current++;
    };
  }, [filters, refresh]);
  const chart = useMemo(
    () =>
      (data?.daily || []).map((point) => ({
        ...point,
        income: Number(point.income_units) / 1e6,
        expense: Number(point.expense_units) / 1e6,
        margin: Number(point.margin_units) / 1e6,
      })) || [],
    [data]
  );
  const s = data?.summary;
  const cards = s
    ? [
        ['调用收入', financeYuan(s.income_units), '已结算调用的实际扣费'],
        ['供应商成本', financeYuan(s.expense_units), '按调用时的成本配置计算'],
        ['毛利（估算）', financeYuan(s.margin_units), '调用收入减配置成本'],
        ['充值入账', financeYuan(s.recharge_units), '钱包入账，非现金到账证明'],
        [
          '已结算调用',
          Number(s.settled_calls).toLocaleString(),
          '按结算完成时间统计',
        ],
        [
          '区间待核算 / 处理中',
          Number(s.pending_calls).toLocaleString(),
          `区间发起调用，当前冻结 ${financeYuan(s.frozen_units)}`,
        ],
      ]
    : [];
  return (
    <>
      <h2>运营总览</h2>
      <p>
        按北京时间统计已结算调用。预占和释放不计入收入、支出；供应商未录入费用不包含在成本中。
      </p>
      <Form
        className='finance-filter'
        onSubmit={() => setFilters({ ...draft })}
      >
        <Form.Group widths='equal'>
          <Form.Input
            type='date'
            required
            id='finance-from'
            label='开始日期'
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
          <Form.Input
            type='date'
            required
            id='finance-to'
            label='结束日期'
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
          <Form.Input
            type='number'
            min='1'
            id='finance-user'
            label='用户 ID'
            value={draft.user_id}
            onChange={(e) => setDraft({ ...draft, user_id: e.target.value })}
          />
          <Form.Input
            id='finance-model'
            label='模型（精确匹配）'
            value={draft.model}
            onChange={(e) => setDraft({ ...draft, model: e.target.value })}
          />
          <Form.Input
            type='number'
            min='1'
            id='finance-channel'
            label='渠道 ID'
            value={draft.channel_id}
            onChange={(e) => setDraft({ ...draft, channel_id: e.target.value })}
          />
        </Form.Group>
        <Button primary type='submit' loading={loading}>
          筛选
        </Button>
        {[7, 30, 90].map((days) => (
          <Button
            key={days}
            type='button'
            onClick={() => {
              const value = { ...draft, ...dateRange(days) };
              setDraft(value);
              setFilters(value);
            }}
          >
            近 {days} 天
          </Button>
        ))}
        <Button
          type='button'
          onClick={() => {
            const value = {
              ...dateRange(),
              user_id: '',
              model: '',
              channel_id: '',
            };
            setDraft(value);
            setFilters(value);
          }}
        >
          重置
        </Button>
      </Form>
      {error && <Message negative content={error} />}{' '}
      {loading && <p role='status'>正在加载统计…</p>}
      {s && (
        <>
          <div className='finance-cards'>
            {cards.map(([name, value, note]) => (
              <article key={name}>
                <span>{name}</span>
                <strong>{value}</strong>
                <small>{note}</small>
              </article>
            ))}
          </div>
          {s.zero_cost_calls > 0 && (
            <Message
              warning
              content={`有 ${s.zero_cost_calls} 次已结算调用的成本配置为 0。毛利仅供参考，不能据此判断实际利润。`}
            />
          )}
          {(filters.model || filters.channel_id) && (
            <Message
              info
              content='充值流水没有模型或渠道归属，充值入账卡只按当前时间和用户筛选。'
            />
          )}
          <section className='finance-chart'>
            <h3>收入与支出趋势</h3>
            <p>单位：人民币 / 日；无调用日期补零。</p>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <LineChart
                  data={chart}
                  margin={{ top: 12, right: 20, left: 12, bottom: 12 }}
                >
                  <CartesianGrid strokeDasharray='3 3' stroke='#e8edf5' />
                  <XAxis
                    dataKey='date'
                    tickFormatter={(value) => value.slice(5)}
                    minTickGap={28}
                  />
                  <YAxis
                    width={85}
                    tickFormatter={(value) =>
                      `¥${
                        Math.abs(value) < 0.01
                          ? value.toFixed(4)
                          : value.toLocaleString()
                      }`
                    }
                  />
                  <Tooltip
                    formatter={(value, name, item) => [
                      financeYuan(item.payload[`${item.dataKey}_units`]),
                      name,
                    ]}
                  />
                  <Legend />
                  <Line
                    dataKey='income'
                    name='调用收入'
                    stroke='#5277ed'
                    dot={false}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                  <Line
                    dataKey='expense'
                    name='供应商成本'
                    stroke='#d98146'
                    dot={false}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                  <Line
                    dataKey='margin'
                    name='毛利（估算）'
                    stroke='#319970'
                    dot={false}
                    strokeWidth={2}
                    strokeDasharray='5 3'
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {s.settled_calls === 0 && <p>当前筛选范围内没有已结算调用。</p>}
          </section>
          <div className='finance-token-summary'>
            实际输入 Token：{Number(s.input_tokens).toLocaleString()} · 实际输出
            Token：{Number(s.output_tokens).toLocaleString()}（含计入输出的思考
            Token）
          </div>
        </>
      )}
    </>
  );
}
